import { useEffect, useMemo } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'

import useCurrentUserId from '@shared/hooks/src/useCurrentUserId'
import { Indicator, updateIndicator } from '@shared/hooks/src/useIndicator'
import usePubNub, { Channels } from '@shared/hooks/src/usePubNub'
import MeSharedProvider from '@shared/providers/src/MeProvider'
import { queryClient } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { QK } from '@shared/utils'

import { useLanguage } from '@providers/LanguageProvider'
import Branch from '@utils/Branch'

/**
 * Wraps shared MeProvider with a query to fetch the current user,
 * and subscribes to PubNub events for the current user.
 */
export default function MeSuspenseProvider({ children }) {
  const userId = useCurrentUserId()

  const [_locale, setLocale] = useLanguage()

  const { data: patient } = useSuspenseQuery({
    queryKey: QK.me(userId).details,
    queryFn: () => API.me.details(),
  })

  const { data: photo } = useQuery({
    queryKey: QK.patients.id(userId).photoID,
    queryFn: () => API.patients.id(userId).photoID.details(),
  })

  useEffect(() => {
    if (window.analytics) {
      window.analytics.identify(userId)
    }
    Branch.setIdentity(userId)
  }, [userId])

  useEffect(() => {
    setLocale(patient.language === 'Spanish' ? 'es' : 'en')
  }, [patient.language, setLocale])

  usePubNub(`user_${userId}`, ({ action, attributes }) => {
    // If a new message is received, update the unread messages count
    if (action === Channels.NewMessage) {
      updateIndicator(userId, Indicator.TotalUnreadMessageCount, undefined, { count: attributes.total_unread_message_count })
    }
    if ([Channels.ReassignedPatient, Channels.EmailVerified].includes(action)) {
      queryClient.invalidateQueries({ queryKey: QK.me(userId).details })
    }
  })

  const data = useMemo(() => {
    return { ...patient, photoId: photo?.url }
  }, [photo, patient])

  return <MeSharedProvider value={data}>{children}</MeSharedProvider>
}
